import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import useLoadingLore from '../hooks/useLoadingLore';
import { useI18n } from '../i18n';
import './MentalLoadScreen.css';

const FALLBACK_CONSOLE = {
  es: {
    verbs: ["Enfrentando", "Hackeando", "Decodificando", "Navegando", "Analizando"],
    nouns: ["el Miedo", "el Vacío", "el Caos", "la Incertidumbre", "la Resistencia"],
    actions: ["Tirando dados de voluntad...", "Calibrando sincronía...", "Escaneando frecuencias...", "Abriendo brecha..."]
  },
  en: {
    verbs: ["Facing", "Hacking", "Decoding", "Navigating", "Analyzing"],
    nouns: ["Fear", "the Void", "Chaos", "Uncertainty", "Resistance"],
    actions: ["Rolling willpower dice...", "Calibrating sync...", "Scanning frequencies...", "Opening breach..."]
  }
};

const asList = (value, fallback) => (Array.isArray(value) && value.length ? value : fallback);
const pick = (items) => items[Math.floor(Math.random() * items.length)];

/**
 * Rotaciones exactas para que cada cara del D20 quede al frente.
 * Estas rotaciones contrarrestan los ángulos de ensamblaje del CSS.
 * Para ver una cara plana: rotateX = -FaceRotateX, rotateY = -FaceRotateY
 */
const DICE_ROTATIONS = [
  // Tapa superior (Ensambladas con rotateX 52.62)
  { face: 1, x: -52.62, y: 0 },
  { face: 2, x: -52.62, y: -72 },
  { face: 3, x: -52.62, y: -144 },
  { face: 4, x: -52.62, y: -216 },
  { face: 5, x: -52.62, y: -288 },
  // Anillo central superior (Ensambladas con rotateX 10.81)
  { face: 6, x: -10.81, y: -36 },
  { face: 7, x: -10.81, y: -108 },
  { face: 8, x: -10.81, y: -180 },
  { face: 9, x: -10.81, y: -252 },
  { face: 10, x: -10.81, y: -324 },
  // Anillo central inferior (Ensambladas con rotateX -10.81 y rotateZ 180)
  { face: 11, x: 10.81, y: 0 },
  { face: 12, x: 10.81, y: -72 },
  { face: 13, x: 10.81, y: -144 },
  { face: 14, x: 10.81, y: -216 },
  { face: 15, x: 10.81, y: -288 },
  // Tapa inferior (Ensambladas con rotateX -52.62 y rotateZ 180)
  { face: 16, x: 52.62, y: -36 },
  { face: 17, x: 52.62, y: -108 },
  { face: 18, x: 52.62, y: -180 },
  { face: 19, x: 52.62, y: -252 },
  { face: 20, x: 52.62, y: -324 },
];

const DiceFace = ({ value }) => {
  // Las caras 11 a 20 están invertidas en el CSS (rotateZ 180)
  const isInverted = value >= 11;
  return (
    <div className={`dice-face face-${value}`}>
      <svg className="dice-triangle-svg" viewBox="0 0 100 86.6">
        <polygon points="50,0 0,86.6 100,86.6" />
      </svg>
      <div className="face-content">
        <span className={`face-number ${isInverted ? 'inverted' : ''}`}>{value}</span>
      </div>
    </div>
  );
};

export default function MentalLoadScreen({
  loading,
  message,
  showSpinner = false,
  spinnerVariant = 'green',
  showDice = true
}) {
  const { user } = useAuth();
  const { language, t } = useI18n();
  const resolvedMessage = message || t('common.generating');
  const [consoleLines, setConsoleLines] = useState([]);
  const [diceRotation, setDiceRotation] = useState({ x: 0, y: 0 });
  const [currentFace, setCurrentFace] = useState(1);
  const loreMessage = useLoadingLore(loading);

  const timerRef = useRef(null);
  const rollTimerRef = useRef(null);

  const optimismBonus = user?.qProfileParams?.optimism ? 2 : 0;

  useEffect(() => {
    if (loading) {
      const fallback = FALLBACK_CONSOLE[language] || FALLBACK_CONSOLE.es;
      const verbs = asList(t('mentalLoad.verbs'), fallback.verbs);
      const nouns = asList(t('mentalLoad.nouns'), fallback.nouns);
      const actions = asList(t('mentalLoad.actions'), fallback.actions);

      setConsoleLines([t('mentalLoad.startingSequence', { message: resolvedMessage.toUpperCase() })]);

      // Procedural console text loop
      timerRef.current = setInterval(() => {
        setConsoleLines(prev => {
          const trait = user?.user_traits?.[0] || t('mentalLoad.traitFallback');
          const verb = pick(verbs);
          const noun = pick(nouns);
          const action = pick(actions);

          const newLine = Math.random() > 0.5
            ? `> ${verb} ${noun} (${trait})...`
            : `> ${action}`;

          const next = [...prev, newLine];
          return next.length > 5 ? next.slice(1) : next;
        });
      }, 2500);

      // Continuous Dice Roll Logic (D20)
      if (showDice) {
        const rollDice = () => {
          if (!loading || !showDice) return;

          const randomFaceIndex = Math.floor(Math.random() * DICE_ROTATIONS.length);
          const target = DICE_ROTATIONS[randomFaceIndex];

          // Vueltas completas para dinamismo (múltiplos de 360)
          const extraRoundsX = (Math.floor(Math.random() * 3) + 3) * 360;
          const extraRoundsY = (Math.floor(Math.random() * 3) + 3) * 360;

          setDiceRotation(prev => {
             // Normalizar rotación actual para que el incremento sea siempre fluido
             const baseRotationX = Math.floor(prev.x / 360) * 360;
             const baseRotationY = Math.floor(prev.y / 360) * 360;

             return {
               x: baseRotationX + extraRoundsX + target.x,
               y: baseRotationY + extraRoundsY + target.y
             };
          });

          setCurrentFace(target.face);

          // Tirada continua: 1.5s animación + 1.2s pausa = 2.7s
          rollTimerRef.current = setTimeout(rollDice, 2700);
        };

        rollDice();
      }

      return () => {
        clearInterval(timerRef.current);
        clearTimeout(rollTimerRef.current);
      };
    }
  }, [loading, resolvedMessage, user, language, t, showDice]);

  if (!loading) return null;

  return (
    <motion.div
      className={`mental-load-overlay ${showSpinner ? 'hybrid' : ''} ${spinnerVariant === 'multicolor' ? 'is-painting' : ''}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="mental-load-content">
        {showSpinner && (
          <div className={`hybrid-loading-top ${spinnerVariant}`}>
            <div className="mutantbox-spinner" />
            <div className="mutantbox-overlay-text">{resolvedMessage}</div>
          </div>
        )}

        <div className="retro-console">
          {consoleLines.map((line, i) => (
            <div key={i} className="console-line">{line}</div>
          ))}
          <motion.div
            animate={{ opacity: [1, 0] }}
            transition={{ repeat: Infinity, duration: 0.8 }}
            className="console-cursor"
          >
            _
          </motion.div>
        </div>

        <div className="lore-container">
          <AnimatePresence mode="wait">
            <motion.div
              key={loreMessage}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
              className="lore-message"
            >
              {loreMessage}
            </motion.div>
          </AnimatePresence>
        </div>

        {showDice && (
          <div className="dice-3d-container">
            <div className="dice-scene">
              <motion.div
                className="dice-icosahedron"
                animate={{
                  rotateX: diceRotation.x,
                  rotateY: diceRotation.y
                }}
                transition={{
                  duration: 1.5,
                  ease: "backOut"
                }}
              >
                {Array.from({ length: 20 }, (_, i) => (
                  <DiceFace key={i + 1} value={i + 1} />
                ))}
              </motion.div>
            </div>
            <div className="roll-info">
              <span className="roll-label">{t('mentalLoad.qWill')}: {currentFace + optimismBonus}</span>
              {optimismBonus > 0 && <span className="bonus-text"> (+{optimismBonus} {t('mentalLoad.optimism')})</span>}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
